import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Material } from '@babylonjs/core/Materials/material';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';

/**
 * Turns a model-backed mesh's merged, `preserveMaterials`-built `MultiMaterial`
 * (`ModelLibrary.ts`, `LoadModelOptions.preserveMaterials`) into this one instance's own accent
 * — never the template's material, and never another instance's.
 *
 * Originally `AvatarManager.applyAvatarMaterial`, generalised here — with `tintSlotName` and
 * `onMissingTintSlot` pulled out as parameters — so `ToolPropRenderer`'s marker
 * (`Mat.Prop.Tint`) can reuse the identical clone-then-recolour shape rather than a second,
 * hand-copied implementation that could silently drift out of step with it. See
 * `plan/open-items.md`, "The whiteboard marker model is authored and unused", for why this had
 * to become a parameter rather than a second hardcoded name: the avatar convention hardcodes
 * `Mat.Avatar.Tint`, and the marker's own slot is named `Mat.Prop.Tint`.
 *
 * CLAUDE.md's shared-material trap ("`Mesh.clone` does not clear the parent...", and separately,
 * the `TransformNode.dispose()` material-leak note) has caused four separate bugs in this
 * project already, all from treating something Babylon hands back by reference as though it were
 * already this caller's own copy. `Mesh.clone()` copies `.material` by reference
 * (`Mesh._copySource`: `this.material = source.material`), so every clone of a given template
 * starts out pointing at the exact same `MultiMaterial` — and the exact same sub-materials — the
 * template itself holds. Recolouring that shared tint-slot instance in place would tint every
 * instance sharing the template at once, the same class of bug by name.
 *
 * The fix: build a brand new `MultiMaterial`, and a brand new sub-material for every slot — a
 * fresh `StandardMaterial` for the slot named `tintSlotName` (recoloured to `tintColor` here),
 * and a `.clone()` of every other slot — so no caller's mesh ever again points at anything the
 * template or another instance still holds, even a slot nothing here touches.
 *
 * Falls back to the documented "tint the whole mesh" behaviour — `mesh.material =
 * fallbackMaterial`, a shared per-instance `StandardMaterial` the caller already built for its
 * own primitive fallback path — whenever no `tintSlotName` slot exists at all: a `MultiMaterial`
 * with no matching name (an older or hand-edited model), or no `MultiMaterial` at all (a
 * template built without `preserveMaterials`). A model-backed mesh must never fail to render
 * over a naming mismatch, so this always leaves `mesh.material` set to something usable, calling
 * `onMissingTintSlot()` exactly once per call in that case so the caller can log or dedupe
 * however it needs to, and returns `undefined` — telling the caller there is no per-instance
 * accent material to attach anything further (a `VisorVisualizer`, for instance) to — only then.
 */
export function applyTintedMaterial(
  scene: Scene,
  mesh: Mesh,
  tintSlotName: string,
  instanceName: string,
  tintColor: Color3,
  fallbackMaterial: StandardMaterial,
  onMissingTintSlot: () => void,
): StandardMaterial | undefined {
  const source = mesh.material;

  if (source instanceof MultiMaterial) {
    const clonedSubMaterials: Array<Material | null> = [];
    let tintMaterial: StandardMaterial | undefined;

    for (const sub of source.subMaterials) {
      if (!sub) {
        clonedSubMaterials.push(null);
        continue;
      }

      if (sub.name === tintSlotName) {
        const tint = new StandardMaterial(`tint-${tintSlotName}-${instanceName}`, scene);
        tint.diffuseColor = tintColor;
        tint.emissiveColor = tintColor.scale(0.25);
        tint.specularColor = Color3.Black();
        clonedSubMaterials.push(tint);
        tintMaterial = tint;
      } else {
        clonedSubMaterials.push(sub.clone(`${sub.name}-${instanceName}`));
      }
    }

    if (tintMaterial) {
      const clonedMulti = new MultiMaterial(`multi-${instanceName}`, scene);
      clonedMulti.subMaterials = clonedSubMaterials;
      mesh.material = clonedMulti;
      return tintMaterial;
    }

    // A MultiMaterial with every slot present except the one named tintSlotName — nothing above
    // is kept; each entry was only ever held by this local array, so disposing them here is
    // enough, with nothing left dangling for the caller's own dispose path to find later.
    for (const cloned of clonedSubMaterials) {
      cloned?.dispose();
    }
  }

  onMissingTintSlot();
  mesh.material = fallbackMaterial;
  return undefined;
}
